/**
 * Settings queries and mutations.
 *
 * Every mutation returns the updated `UserProfileDto`, so the profile cache is written from the response instead of
 * being invalidated: the server already told the SPA the new state, and a refetch would add a round-trip and a flash
 * of stale data.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  aiApi,
  usersApi,
  type UpdateAiPreferencesPayload,
  type UpdateProfilePayload,
} from '@/features/settings/api/settingsApi';
import { authApi } from '@/features/auth/api/authApi';
import { queryKeys } from '@/lib/queryKeys';
import { useAuthStore } from '@/stores/authStore';
import type { UserProfile } from '@/types/api';

/** Reads the profile of the signed-in account. */
export function useProfile(enabled = true) {
  return useQuery({
    queryKey: queryKeys.profile,
    enabled,
    queryFn: usersApi.getProfile,
  });
}

/** Updates the profile and keeps both the query cache and the auth store in step. */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateProfilePayload) => usersApi.updateProfile(payload),
    onSuccess: (profile: UserProfile) => {
      queryClient.setQueryData(queryKeys.profile, profile);
      useAuthStore.getState().setUser(profile);
    },
  });
}

/** Stores the default AI credential, provider, model and prompt level (TASK-0007). */
export function useUpdateAiPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateAiPreferencesPayload) => usersApi.updateAiPreferences(payload),
    onSuccess: (profile: UserProfile) => {
      queryClient.setQueryData(queryKeys.profile, profile);
      useAuthStore.getState().setUser(profile);
    },
  });
}

/**
 * Registers the public identifier of the signed-in account, once (TASK-0060).
 *
 * The answer is the whole profile, so both caches are written from the response rather than invalidated — the same rule
 * the two mutations above follow. There is deliberately no "rename" twin: changing a handle is an administrative act, and
 * the settings screen offers no path to it.
 */
export function useRegisterPublicIdentifier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (publicIdentifier: string) => authApi.registerPublicIdentifier(publicIdentifier),
    onSuccess: (profile: UserProfile) => {
      queryClient.setQueryData(queryKeys.profile, profile);
      useAuthStore.getState().setUser(profile);
    },
  });
}

/** Reads the provider and model catalog; the answer is effectively static, so it is cached for a day. */
export function useAiProviders() {
  return useQuery({
    queryKey: queryKeys.aiProviders,
    queryFn: aiApi.getProviders,
    staleTime: 24 * 60 * 60 * 1000,
  });
}
